import React, { useState } from 'react';
import Botpoison from '@botpoison/browser';
import './Contact.css';

// Create Botpoison instance with the provided public key
const botpoison = new Botpoison({
  publicKey: "pk_934ba39b-44d9-4a33-95f3-734ccabde09b"
});

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: '',
    loading: false
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setFormStatus({ submitted: false, error: false, message: '', loading: true });

    try {
      // Process Botpoison challenge
      const { solution } = await botpoison.challenge();

      // Submit to Formspark
      const response = await fetch('https://submit-form.com/iKPBbZDua', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          _botpoison: solution,
          _email: {
            to: 'warren.hidev@gmail.com',
            subject: 'New Contact Form Submission from Tri-Bay Electric Website'
          }
        }),
      });

      if (response.ok) {
        setFormStatus({
          submitted: true,
          error: false,
          message: 'Thank you for your message. We will contact you shortly!',
          loading: false
        });
        // Reset form data
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({
        submitted: true,
        error: true,
        message: 'Sorry, there was an error sending your message. Please try again or contact us directly.',
        loading: false
      });
    }
  };

  // Embedded Google Map of the new office location - 2016 Hartog Drive San Jose CA 95131
  const mapEmbedUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3170.073282974934!2d-121.88985768469213!3d37.40423297982737!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x808fcbf7e2e2e2e3%3A0x7e2e2e2e2e2e2e2e!2s2016%20Hartog%20Dr%2C%20San%20Jose%2C%20CA%2095131!5e0!3m2!1sen!2sus!4v1716400000000!5m2!1sen!2sus";

  return (
    <section id="contact" className="contact-section section-alt">
      <div className="container">
        <div className="section-header">
          <h2>Contact Us</h2>
          <p className="section-intro">
            Get in touch with our team for any inquiries or to request a quote for your project.
          </p>
        </div>

        <div className="contact-container">
          <div className="contact-form-container">
            {formStatus.submitted ? (
              <div className={`form-message ${formStatus.error ? 'error' : 'success'}`}>
                {formStatus.message}
                {!formStatus.error && (
                  <button
                    onClick={() => setFormStatus({ submitted: false, error: false, message: '', loading: false })}
                    className="reset-form-button"
                  >
                    Send Another Message
                  </button>
                )}
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Name <span className="required">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={formStatus.loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email <span className="required">*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={formStatus.loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={formStatus.loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message <span className="required">*</span></label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    required
                    disabled={formStatus.loading}
                  ></textarea>
                </div>

                <button type="submit" className="submit-button" disabled={formStatus.loading}>
                  {formStatus.loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          <div className="contact-info-map-container">
            <div className="map-container">
              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Tri-Bay Electric Office Location"
              ></iframe>
            </div>

            <div className="contact-info-container">
              <div className="contact-info-section">
                <h3>Address</h3>
                <p>2016 Hartog Drive</p>
                <p>San Jose, CA 95131</p>
              </div>

              <div className="contact-info-section">
                <h3>Phone</h3>
                <p>
                  <a href="tel:4083920014" className="contact-link">Ph. 408-392-0014</a>
                </p>
              </div>

              <div className="contact-info-section">
                <h3>Email</h3>
                <p>
                  <strong>Don Hoque</strong><br />
                  <a href="mailto:don@tribayelectric.com" className="contact-link">don@tribayelectric.com</a>
                </p>
                <p>
                  <strong>Brian Keaney</strong><br />
                  <a href="mailto:brian@tribayelectric.com" className="contact-link">brian@tribayelectric.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 